/**
 * Bearer-token provider abstraction + real Cognito implementation.
 *
 * Pure TS — no React. The EngineeringVector API Gateway is Cognito-authed; the
 * ApiClient attaches `Authorization: Bearer <token>` on every request using the
 * token returned by {@link TokenProvider.getToken}.
 *
 * {@link CognitoTokenProvider} implements the OAuth2 Authorization Code + PKCE
 * flow against the AmazonFederate (Midway) hosted UI as a PUBLIC client (no
 * secret). Authentication is exclusively via this OAuth flow — there is no
 * bypass; the API is only reachable with real Cognito id/access tokens.
 */

import { getCognitoConfig, type CognitoConfig } from '../config/apiConfig';

export interface TokenProvider {
  getToken(): Promise<string>;
}

/**
 * Thrown when there is no usable session (never signed in, or the session
 * expired and could not be refreshed). The app catches this to prompt sign-in.
 */
export class NotAuthenticatedError extends Error {
  constructor(message = 'Not signed in') {
    super(message);
    this.name = 'NotAuthenticatedError';
    // Restore prototype chain for instanceof across transpile targets.
    Object.setPrototypeOf(this, NotAuthenticatedError.prototype);
  }
}

/** localStorage keys (persist across tabs/reloads). */
const LS = {
  idToken: 'ev.auth.id_token',
  accessToken: 'ev.auth.access_token',
  refreshToken: 'ev.auth.refresh_token',
  expiresAt: 'ev.auth.expires_at',
} as const;

/** sessionStorage keys (per-tab, cleared after the code exchange). */
const SS = {
  pkceVerifier: 'ev.auth.pkce_verifier',
  oauthState: 'ev.auth.oauth_state',
} as const;

/** Clock-skew buffer: treat a token as expired 60s early. */
const EXPIRY_SKEW_MS = 60_000;

/** Shape of the Cognito /oauth2/token response. */
interface TokenEndpointResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
}

/* ---------- PKCE / base64url helpers (browser Web Crypto) ---------- */

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength = 32): string {
  const arr = new Uint8Array(byteLength);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function pkceChallengeFromVerifier(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Real Cognito token provider (Authorization Code + PKCE, public client).
 */
export class CognitoTokenProvider implements TokenProvider {
  private readonly config: CognitoConfig;

  constructor(config: CognitoConfig = getCognitoConfig()) {
    this.config = {
      ...config,
      hostedUiDomain: config.hostedUiDomain.replace(/\/+$/, ''),
    };
  }

  /* ---------------- public sign-in lifecycle ---------------- */

  /**
   * Begins sign-in: generates a PKCE verifier/challenge (verifier stashed in
   * sessionStorage), then redirects the browser to the hosted-UI authorize
   * endpoint, jumping straight to AmazonFederate.
   */
  async login(): Promise<void> {
    const verifier = randomUrlSafe();
    const challenge = await pkceChallengeFromVerifier(verifier);
    const state = randomUrlSafe(16);
    sessionStorage.setItem(SS.pkceVerifier, verifier);
    sessionStorage.setItem(SS.oauthState, state);

    const params = new URLSearchParams({
      response_type: this.config.responseType,
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(' '),
      state,
      identity_provider: this.config.identityProvider,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    window.location.assign(
      `${this.config.hostedUiDomain}/oauth2/authorize?${params.toString()}`,
    );
  }

  /**
   * Completes sign-in on the /global/auth/callback route: validates state,
   * exchanges the authorization code (+ PKCE verifier) for tokens, and persists
   * them to localStorage.
   */
  async handleRedirectCallback(): Promise<void> {
    const url = new URL(window.location.href);
    const error = url.searchParams.get('error');
    if (error) {
      const desc = url.searchParams.get('error_description');
      throw new NotAuthenticatedError(
        `OAuth error: ${error}${desc ? ` (${desc})` : ''}`,
      );
    }

    const code = url.searchParams.get('code');
    if (!code) {
      throw new NotAuthenticatedError('No authorization code in callback URL');
    }

    const returnedState = url.searchParams.get('state');
    const expectedState = sessionStorage.getItem(SS.oauthState);
    if (expectedState && returnedState && expectedState !== returnedState) {
      throw new NotAuthenticatedError('OAuth state mismatch — possible CSRF');
    }

    const verifier = sessionStorage.getItem(SS.pkceVerifier);
    if (!verifier) {
      throw new NotAuthenticatedError('Missing PKCE verifier for code exchange');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: verifier,
    });

    const json = await this.postToken(body);
    this.persistTokenResponse(json);
    sessionStorage.removeItem(SS.pkceVerifier);
    sessionStorage.removeItem(SS.oauthState);
  }

  /** Clears the local session and redirects to the hosted-UI logout endpoint. */
  logout(): void {
    const hadSession =
      !!localStorage.getItem(LS.idToken) ||
      !!localStorage.getItem(LS.accessToken);
    this.clearTokens();

    if (!hadSession) {
      // Nothing to sign out of — no hosted-UI round-trip needed.
      return;
    }
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      logout_uri: this.config.logoutUri,
    });
    window.location.assign(
      `${this.config.hostedUiDomain}/logout?${params.toString()}`,
    );
  }

  /* ---------------- TokenProvider contract ---------------- */

  /**
   * Returns a valid bearer token for the API: the current access/id token,
   * refreshing first if it has expired. Throws {@link NotAuthenticatedError}
   * when no usable session exists.
   */
  async getToken(): Promise<string> {
    if (this.isExpired()) {
      await this.refresh();
    }
    // The API Gateway method sets authorizationScopes=["openid"], so API
    // Gateway requires an ACCESS token whose `scope` claim contains `openid`
    // and REJECTS ID tokens (they carry no `scope` claim). Our login requests
    // the `openid` scope, so the access token satisfies this. Prefer it; fall
    // back to the id token only if no access token is present.
    const token =
      localStorage.getItem(LS.accessToken) ?? localStorage.getItem(LS.idToken);
    if (!token) {
      throw new NotAuthenticatedError();
    }
    return token;
  }

  /* ---------------- helpers used by identity + UI ---------------- */

  /**
   * The raw token whose claims describe the user: the Cognito id token. Used by
   * the identity provider to derive name/alias.
   */
  getRawIdentityToken(): string | null {
    return localStorage.getItem(LS.idToken) ?? null;
  }

  /**
   * Synchronous best-effort check for UI gating. True if any real token exists
   * (even if expired — a refresh may still succeed).
   */
  isAuthenticated(): boolean {
    return (
      !!localStorage.getItem(LS.idToken) ||
      !!localStorage.getItem(LS.accessToken)
    );
  }

  /* ---------------- internals ---------------- */

  private isExpired(): boolean {
    const raw = localStorage.getItem(LS.expiresAt);
    if (!raw) return true;
    const expiresAt = Number(raw);
    if (Number.isNaN(expiresAt)) return true;
    return Date.now() >= expiresAt - EXPIRY_SKEW_MS;
  }

  private async refresh(): Promise<void> {
    const refreshToken = localStorage.getItem(LS.refreshToken);
    if (!refreshToken) {
      throw new NotAuthenticatedError('Session expired; please sign in again');
    }
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      refresh_token: refreshToken,
    });
    let json: TokenEndpointResponse;
    try {
      json = await this.postToken(body);
    } catch {
      this.clearTokens();
      throw new NotAuthenticatedError('Failed to refresh session');
    }
    // A refresh response typically omits refresh_token; preserve the existing.
    if (!json.refresh_token) {
      json.refresh_token = refreshToken;
    }
    this.persistTokenResponse(json);
  }

  private async postToken(
    body: URLSearchParams,
  ): Promise<TokenEndpointResponse> {
    const resp = await fetch(`${this.config.hostedUiDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      let message = `${resp.status} ${resp.statusText}`;
      try {
        const parsed = JSON.parse(text) as TokenEndpointResponse;
        if (parsed.error) message = parsed.error;
      } catch {
        if (text) message = text;
      }
      throw new NotAuthenticatedError(`Token endpoint error: ${message}`);
    }
    return (text ? JSON.parse(text) : {}) as TokenEndpointResponse;
  }

  private persistTokenResponse(json: TokenEndpointResponse): void {
    const expiresAt = Date.now() + (json.expires_in ?? 3600) * 1000;
    if (json.id_token) localStorage.setItem(LS.idToken, json.id_token);
    if (json.access_token) {
      localStorage.setItem(LS.accessToken, json.access_token);
    }
    if (json.refresh_token) {
      localStorage.setItem(LS.refreshToken, json.refresh_token);
    }
    localStorage.setItem(LS.expiresAt, String(expiresAt));
  }

  private clearTokens(): void {
    localStorage.removeItem(LS.idToken);
    localStorage.removeItem(LS.accessToken);
    localStorage.removeItem(LS.refreshToken);
    localStorage.removeItem(LS.expiresAt);
    sessionStorage.removeItem(SS.pkceVerifier);
    sessionStorage.removeItem(SS.oauthState);
  }
}

/** Default token provider singleton used by the API facade AND the auth UI. */
export const cognitoTokenProvider = new CognitoTokenProvider();

export default cognitoTokenProvider;
